import { usePathname, useRouter } from 'expo-router';
import React from 'react';
import { Alert, Platform, Pressable, View } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';

import { useCustomerDelete } from '@/api/customers/use-customer-delete';
import { isDesktop, signOut } from '@/core';
import { ERROR_DARK } from '@/style/colors';
import { Card, showErrorMessage, Text } from '@/ui';
import { ConfirmModal } from '@/ui/confirm-modal';
import { Email, Lock, Person } from '@/ui/icons';
import { Call } from '@/ui/icons/call';
import { ChevronRight } from '@/ui/icons/chevron-right';
import { MenuItem } from '@/ui/menu-item';

import { NavigationButton, NavigationButtonTypes } from '../navigation-button';

export default function Profile() {
  const { styles, theme } = useStyles(stylesheet);
  const router = useRouter();
  const pathname = usePathname();
  const [deleteAccountModalVisible, setDeleteAccountModalVisible] =
    React.useState(false);

  const { mutateAsync: deleteCustomer, isPending: isDeletingCustomer } =
    useCustomerDelete();

  const handleDeleteAccount = () => {
    if (Platform.OS === 'web') {
      setDeleteAccountModalVisible(true);
      return;
    }

    Alert.alert(
      'Delete account and data',
      'Are you sure you want to delete your account and all data?',
      [
        {
          text: 'Cancel',
        },
        {
          text: 'Delete',
          onPress: async () => {
            try {
              await deleteCustomer({});
              signOut();
              router.replace('/search');
            } catch (e) {
              showErrorMessage(e.message);
            }
          },
          style: 'destructive',
        },
      ]
    );
  };

  return (
    <>
      <View
        style={{
          flexGrow: 1,
          backgroundColor: theme.colors.background_1,
          alignItems: 'center',
        }}
      >
        <View style={styles.container}>
          {isDesktop() ? (
            <View style={{ gap: 16 }}>
              {!pathname.includes('partner') ? (
                <NavigationButton
                  type={NavigationButtonTypes.back_with_text}
                  onPress={() => {
                    if (pathname.includes('partner')) {
                      router.push('/partner/more');
                    } else {
                      router.push('/account');
                    }
                  }}
                />
              ) : null}

              <Text type="largeTitle">Profile</Text>
            </View>
          ) : null}

          <Card>
            <MenuItem
              leftIcon={<Person />}
              leftIconFillColor={theme.colors.icon.color}
              rightIcon={<ChevronRight />}
              rightIconFillColor={theme.colors.icon.color}
              style={{ width: '100%' }}
              tx="profile.name"
              onPress={() => {
                if (pathname.includes('partner')) {
                  router.push('/partner/more/profile/name');
                } else {
                  router.push('/account/profile/name');
                }
              }}
            />
          </Card>

          <Card>
            <MenuItem
              leftIcon={<Email />}
              leftIconFillColor={theme.colors.icon.color}
              rightIcon={<ChevronRight />}
              rightIconFillColor={theme.colors.icon.color}
              style={{ width: '100%' }}
              tx="profile.email"
              onPress={() => {
                if (pathname.includes('partner')) {
                  router.push('/partner/more/profile/email');
                } else {
                  router.push('/account/profile/email');
                }
              }}
            />

            <MenuItem
              leftIcon={<Call />}
              leftIconFillColor={theme.colors.icon.color}
              rightIcon={<ChevronRight />}
              rightIconFillColor={theme.colors.icon.color}
              style={{ width: '100%' }}
              tx="profile.phoneNumber"
              onPress={() => {
                if (pathname.includes('partner')) {
                  router.push('/partner/more/profile/phone');
                } else {
                  router.push('/account/profile/phone');
                }
              }}
            />

            <MenuItem
              leftIcon={<Lock />}
              leftIconFillColor={theme.colors.icon.color}
              rightIcon={<ChevronRight />}
              rightIconFillColor={theme.colors.icon.color}
              style={{ width: '100%' }}
              tx="profile.password"
              onPress={() => {
                if (pathname.includes('partner')) {
                  router.push('/partner/more/profile/password');
                } else {
                  router.push('/account/profile/password');
                }
              }}
            />
          </Card>

          <Pressable
            hitSlop={8}
            onPress={handleDeleteAccount}
            style={({ pressed }) => [
              {
                opacity: pressed ? 0.5 : 1,
                backgroundColor: 'transparent',
              },
            ]}
          >
            <Text
              tx="profile.deleteAccount"
              type="bodyRegular"
              style={{ color: ERROR_DARK }}
            />
          </Pressable>
        </View>
      </View>

      <ConfirmModal
        isVisible={deleteAccountModalVisible}
        titleTx="profile.deleteAccountModalTitle"
        bodyTx="profile.deleteAccountModalBody"
        leftButton={{
          tx: 'profile.cancel',
          onPress: () => {
            setDeleteAccountModalVisible(false);
          },
        }}
        rightButton={{
          tx: 'profile.delete',
          onPress: async () => {
            try {
              await deleteCustomer({});
              signOut();
              router.replace('/search');
            } catch (e) {
              showErrorMessage(e.message);
            }

            setDeleteAccountModalVisible(false);
          },
        }}
        onDismiss={() => {
          setDeleteAccountModalVisible(false);
        }}
      />
    </>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  container: {
    flexGrow: 1,
    paddingTop: 24,
    paddingLeft: 16,
    paddingRight: 16,
    gap: 16,
    maxWidth: {
      xs: '100%',
      sm: 600,
      md: 800,
    },
    width: {
      xs: '100%',
      md: 600,
    },
  },
}));
